import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Save, Plus, Trash2, Paperclip, Download, CheckCircle2, Circle,
  Calendar, Clock, Tag, User, Link2, Layers, AlertTriangle, Upload,
  MessageSquare, Send, TrendingUp, Lock, Eye, EyeOff, GitMerge, Search
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback } from './ui/avatar';
import axios from 'axios';
import { getApiUrl } from '../utils/config';
import { toast } from 'react-hot-toast';

const PRIORITY_META = {
  1: { label: 'Low', cls: 'bg-slate-100 text-slate-600' },
  2: { label: 'Medium', cls: 'bg-amber-100 text-amber-700' },
  3: { label: 'High', cls: 'bg-rose-100 text-rose-700' },
};

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
];

const fmt = d => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const initials = v => (v || 'U').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
const fileSize = bytes => bytes < 1024 ? `${bytes}B` : bytes < 1048576 ? `${(bytes / 1024).toFixed(1)}KB` : `${(bytes / 1048576).toFixed(1)}MB`;

const TaskDetailModal = ({
  task,
  members = [],
  sprints = [],
  orgId,
  onClose,
  onUpdate,
  canManage,
  canEditMetadata, // controls Priority, Assignee, Due Date (managers/leads/admins only)
  currentUserId,
}) => {
  // If canEditMetadata not explicitly passed, fall back to canManage
  const metaEditable = canEditMetadata !== undefined ? canEditMetadata : canManage;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('todo');
  const [priority, setPriority] = useState(2);
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimateHrs, setEstimateHrs] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [sprintId, setSprintId] = useState('');

  // Subtasks
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [subtasksLoading, setSubtasksLoading] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentProgress, setCommentProgress] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  // Time logs
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeLogsLoading, setTimeLogsLoading] = useState(false);
  const [logHours, setLogHours] = useState('');
  const [logNote, setLogNote] = useState('');
  const [loggingTime, setLoggingTime] = useState(false);

  // Watchers
  const [watcherData, setWatcherData] = useState({ is_watching: false, count: 0, watchers: [] });
  const [togglingWatch, setTogglingWatch] = useState(false);

  // Dependencies
  const [depTasks, setDepTasks] = useState([]);
  const [depSearch, setDepSearch] = useState('');
  const [depResults, setDepResults] = useState([]);
  const [depSearching, setDepSearching] = useState(false);
  const depDebounce = useRef(null);

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const isOverdue = task?.due_date && task.status !== 'approved' && new Date(task.due_date) < new Date();

  useEffect(() => {
    if (!task) return;
    setTitle(task.title || '');
    setDescription(task.description || '');
    setStatus(task.status || 'todo');
    setPriority(task.priority || 2);
    setAssigneeId(task.assignee_id || '');
    setDueDate(task.due_date ? new Date(task.due_date).toISOString().slice(0, 10) : '');
    setEstimateHrs(task.estimate_hrs != null ? String(task.estimate_hrs) : '');
    setTags(task.tags || []);
    setSprintId(task.sprint_id || '');
    setDirty(false);
    loadSubtasks();
    loadAttachments();
    loadComments();
    loadTimeLogs();
    loadWatchers();
    loadDepTasks();
  }, [task?.id]);

  const loadSubtasks = async () => {
    if (!task?.id) return;
    setSubtasksLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/subtasks`));
      setSubtasks(res.data || []);
    } catch { setSubtasks([]); } finally { setSubtasksLoading(false); }
  };

  const loadAttachments = async () => {
    if (!task?.id) return;
    setAttachmentsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/attachments`));
      setAttachments(res.data || []);
    } catch { setAttachments([]); } finally { setAttachmentsLoading(false); }
  };

  const loadComments = async () => {
    if (!task?.id) return;
    setCommentsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/comments`));
      setComments(res.data || []);
    } catch { setComments([]); } finally { setCommentsLoading(false); }
  };

  const loadTimeLogs = async () => {
    if (!task?.id) return;
    setTimeLogsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/time-logs`));
      setTimeLogs(res.data || []);
    } catch { setTimeLogs([]); } finally { setTimeLogsLoading(false); }
  };

  const submitTimeLog = async () => {
    const h = parseFloat(logHours);
    if (!h || h <= 0) { toast.error('Enter valid hours > 0'); return; }
    setLoggingTime(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/time-logs`), {
        hours: h,
        note: logNote.trim() || null,
      });
      setTimeLogs(prev => [res.data, ...prev]);
      setLogHours('');
      setLogNote('');
      toast.success(`${h}h logged`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log time'); }
    finally { setLoggingTime(false); }
  };

  const deleteTimeLog = async (logId) => {
    try {
      await axios.delete(getApiUrl(`/organizations/${orgId}/time-logs/${logId}`));
      setTimeLogs(prev => prev.filter(l => l.id !== logId));
    } catch { toast.error('Failed to delete log'); }
  };

  const loadWatchers = async () => {
    if (!task?.id) return;
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/watchers`));
      setWatcherData(res.data);
    } catch { setWatcherData({ is_watching: false, count: 0, watchers: [] }); }
  };

  const toggleWatch = async () => {
    setTogglingWatch(true);
    try {
      if (watcherData.is_watching) {
        await axios.delete(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/watch`));
        setWatcherData(p => ({ ...p, is_watching: false, count: Math.max(0, p.count - 1) }));
        toast.success('Unwatched task');
      } else {
        await axios.post(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/watch`));
        setWatcherData(p => ({ ...p, is_watching: true, count: p.count + 1 }));
        toast.success('Watching task — you\'ll be notified of updates');
      }
    } catch { toast.error('Failed to update watch status'); }
    finally { setTogglingWatch(false); }
  };

  const loadDepTasks = async () => {
    if (!task?.id || !task.depends_on?.length) { setDepTasks([]); return; }
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/tasks/batch`), {
        params: { ids: task.depends_on.join(',') }
      });
      setDepTasks(res.data || []);
    } catch { setDepTasks([]); }
  };

  const searchDepTasks = async (q) => {
    if (!q.trim() || q.trim().length < 2) { setDepResults([]); return; }
    setDepSearching(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/search`), { params: { q: q.trim() } });
      const filtered = (res.data?.tasks || []).filter(t => t.id !== task.id && !task.depends_on?.includes(t.id));
      setDepResults(filtered.slice(0, 8));
    } catch { setDepResults([]); }
    finally { setDepSearching(false); }
  };

  const addDependency = async (depTask) => {
    const newDeps = [...(task.depends_on || []), depTask.id];
    try {
      await axios.patch(getApiUrl(`/organizations/${orgId}/tasks/${task.id}`), { depends_on: newDeps });
      setDepTasks(prev => [...prev, depTask]);
      setDepSearch('');
      setDepResults([]);
      if (onUpdate) onUpdate({ ...task, depends_on: newDeps });
    } catch { toast.error('Failed to add dependency'); }
  };

  const removeDependency = async (depId) => {
    const newDeps = (task.depends_on || []).filter(id => id !== depId);
    try {
      await axios.patch(getApiUrl(`/organizations/${orgId}/tasks/${task.id}`), { depends_on: newDeps });
      setDepTasks(prev => prev.filter(t => t.id !== depId));
      if (onUpdate) onUpdate({ ...task, depends_on: newDeps });
    } catch { toast.error('Failed to remove dependency'); }
  };

  useEffect(() => {
    clearTimeout(depDebounce.current);
    depDebounce.current = setTimeout(() => searchDepTasks(depSearch), 300);
    return () => clearTimeout(depDebounce.current);
  }, [depSearch]);

  const postComment = async () => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/comments`), {
        task_id: task.id,
        content: commentText.trim(),
        progress: commentProgress !== '' ? parseInt(commentProgress, 10) : null,
      });
      setComments(prev => [...prev, res.data]);
      setCommentText('');
      setCommentProgress('');
    } catch (e) { toast.error('Failed to post comment'); } finally { setPostingComment(false); }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority: Number(priority),
        assignee_id: assigneeId || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        estimate_hrs: estimateHrs ? parseFloat(estimateHrs) : null,
        tags,
        sprint_id: sprintId || null,
      };
      await onUpdate(task.id, payload);
      setDirty(false);
      toast.success('Task saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) { setTags(prev => [...prev, t]); setDirty(true); }
    setTagInput('');
  };
  const removeTag = t => { setTags(prev => prev.filter(x => x !== t)); setDirty(true); };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/tasks`), {
        project_id: task.project_id,
        title: newSubtask.trim(),
        parent_task_id: task.id,
        status: 'todo',
        priority: 2,
      });
      setSubtasks(prev => [...prev, res.data]);
      setNewSubtask('');
    } catch (e) { toast.error('Failed to add subtask'); }
  };

  const toggleSubtask = async (subtask) => {
    const newStatus = subtask.status === 'approved' ? 'todo' : 'approved';
    try {
      const res = await axios.patch(getApiUrl(`/organizations/${orgId}/tasks/${subtask.id}`), { status: newStatus });
      setSubtasks(prev => prev.map(s => s.id === subtask.id ? res.data : s));
    } catch (e) { toast.error('Failed to update subtask'); }
  };

  const deleteSubtask = async (subtaskId) => {
    try {
      await axios.delete(getApiUrl(`/organizations/${orgId}/tasks/${subtaskId}`));
      setSubtasks(prev => prev.filter(s => s.id !== subtaskId));
    } catch (e) { toast.error('Failed to delete subtask'); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/attachments`), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments(prev => [...prev, res.data]);
      toast.success('File uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(getApiUrl(`/organizations/${orgId}/attachments/${attachmentId}`));
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch { toast.error('Failed to delete attachment'); }
  };

  const mark = () => setDirty(true);

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Side drawer */}
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        className="relative ml-auto h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Layers className="w-4 h-4 text-[#5F2AA8] shrink-0" />
            <span className="text-xs text-muted-foreground truncate">{task.project_id}</span>
            {isOverdue && (
              <Badge className="text-[10px] bg-rose-100 text-rose-700 border-rose-200 shrink-0">
                <AlertTriangle className="w-2.5 h-2.5 mr-1" />Overdue
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={toggleWatch} disabled={togglingWatch}
              title={watcherData.is_watching ? 'Unwatch task' : 'Watch task'}
              className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-colors ${watcherData.is_watching ? 'bg-[#f3eeff] border-[#d4b8f0] text-[#4B1E87]' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {watcherData.is_watching ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {watcherData.is_watching ? 'Watching' : 'Watch'}
              {watcherData.count > 0 && <span className="ml-1 font-semibold">{watcherData.count}</span>}
            </button>
            {dirty && (
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs">
                <Save className="w-3 h-3 mr-1" />{saving ? 'Saving…' : 'Save'}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid md:grid-cols-5 h-full">
            {/* Left: title, description, subtasks, attachments */}
            <div className="md:col-span-3 min-w-0 p-6 border-r border-slate-100 space-y-6">
              {/* Title */}
              <div>
                <Input
                  value={title}
                  onChange={e => { setTitle(e.target.value); mark(); }}
                  className="text-base font-semibold border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-400 rounded-none px-0 h-auto py-1"
                  placeholder="Task title"
                  disabled={!canManage}
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</label>
                <Textarea
                  value={description}
                  onChange={e => { setDescription(e.target.value); mark(); }}
                  placeholder="Add a description…"
                  rows={4}
                  disabled={!canManage}
                  className="resize-none text-sm"
                />
              </div>

              {/* Subtasks */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Subtasks ({subtasks.filter(s => s.status === 'approved').length}/{subtasks.length})
                </label>
                {subtasksLoading ? (
                  <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>
                ) : (
                  <div className="space-y-1.5">
                    {subtasks.map(s => (
                      <div key={s.id} className="flex items-center gap-2 group">
                        <button onClick={() => toggleSubtask(s)} className="shrink-0">
                          {s.status === 'approved'
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            : <Circle className="w-4 h-4 text-slate-300" />}
                        </button>
                        <span className={`flex-1 text-sm ${s.status === 'approved' ? 'line-through text-muted-foreground' : ''}`}>
                          {s.title}
                        </span>
                        {canManage && (
                          <button onClick={() => deleteSubtask(s.id)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3.5 h-3.5 text-slate-400 hover:text-rose-500" />
                          </button>
                        )}
                      </div>
                    ))}
                    {canManage && (
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newSubtask}
                          onChange={e => setNewSubtask(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addSubtask()}
                          placeholder="Add subtask…"
                          className="h-7 text-sm"
                        />
                        <Button size="sm" variant="outline" onClick={addSubtask} className="h-7 px-2">
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  Attachments ({attachments.length})
                </label>
                {attachmentsLoading ? (
                  <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>
                ) : (
                  <div className="space-y-1.5">
                    {attachments.map(a => (
                      <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 group">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="flex-1 text-xs truncate">{a.filename}</span>
                        <span className="text-[11px] text-muted-foreground shrink-0">{fileSize(a.size_bytes)}</span>
                        <a
                          href={getApiUrl(`/organizations/${orgId}/attachments/${a.id}/download`)}
                          target="_blank" rel="noreferrer"
                          className="shrink-0"
                        >
                          <Download className="w-3.5 h-3.5 text-[#5F2AA8] hover:text-[#4B1E87]" />
                        </a>
                        {canManage && (
                          <button onClick={() => deleteAttachment(a.id)} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="w-3.5 h-3.5 text-rose-400 hover:text-rose-600" />
                          </button>
                        )}
                      </div>
                    ))}
                    {canManage && (
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={e => handleFileUpload(e.target.files?.[0])}
                        />
                        <Button
                          size="sm" variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="h-7 text-xs gap-1.5"
                        >
                          <Upload className="w-3 h-3" />
                          {uploading ? 'Uploading…' : 'Attach File'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Comments */}
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Comments & Updates ({comments.length})
                </label>
                {commentsLoading ? (
                  <p className="text-xs text-muted-foreground animate-pulse">Loading comments…</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map(c => (
                      <div key={c.id} className="flex gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
                          {(c.created_by_name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-slate-700">{c.created_by_name || 'User'}</span>
                            <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                            {c.progress != null && (
                              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#f3eeff] text-[#4B1E87] border border-[#d4b8f0]">
                                <TrendingUp className="w-2.5 h-2.5" />{c.progress}% done
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                        </div>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <p className="text-xs text-muted-foreground">No comments yet. Be the first to update progress!</p>
                    )}
                  </div>
                )}
                {/* Post comment */}
                <div className="space-y-2 pt-1">
                  <Textarea
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    placeholder="Add a comment or progress update…"
                    rows={2}
                    className="resize-none text-sm"
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                  />
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="number" min="0" max="100"
                        value={commentProgress}
                        onChange={e => setCommentProgress(e.target.value)}
                        placeholder="% done"
                        className="w-16 h-7 text-xs border border-slate-200 rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                    </div>
                    <Button size="sm" onClick={postComment} disabled={postingComment || !commentText.trim()} className="h-7 text-xs gap-1 ml-auto">
                      <Send className="w-3 h-3" />{postingComment ? 'Posting…' : 'Comment'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* ── Time Logging ── */}
              <div className="border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b">
                  <span className="text-xs font-semibold text-gray-700">
                    Time Logged
                    {timeLogs.length > 0 && (
                      <span className="ml-1 text-[#4B1E87]">
                        · {timeLogs.reduce((s, l) => s + l.hours, 0).toFixed(1)}h logged
                        {task?.estimate_hrs ? ` / ${task.estimate_hrs}h estimate` : ''}
                      </span>
                    )}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  {/* Log form */}
                  <div className="flex gap-2">
                    <input
                      type="number" min="0.25" step="0.25" placeholder="Hours"
                      value={logHours} onChange={e => setLogHours(e.target.value)}
                      className="w-20 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <input
                      type="text" placeholder="Note (optional)"
                      value={logNote} onChange={e => setLogNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') submitTimeLog(); }}
                      className="flex-1 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <Button size="sm" onClick={submitTimeLog} disabled={loggingTime || !logHours}
                      className="h-7 text-xs">
                      {loggingTime ? '…' : 'Log'}
                    </Button>
                  </div>
                  {/* Existing logs */}
                  {timeLogsLoading ? (
                    <p className="text-[11px] text-muted-foreground animate-pulse">Loading…</p>
                  ) : timeLogs.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {timeLogs.map(log => (
                        <div key={log.id} className="flex items-center gap-2 text-[11px] text-gray-500 group">
                          <span className="font-semibold text-[#4B1E87] w-10 shrink-0">{log.hours}h</span>
                          <span className="flex-1 truncate">{log.user_name}{log.note ? ` — ${log.note}` : ''}</span>
                          <span className="shrink-0">{log.logged_at ? new Date(log.logged_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                          <button onClick={() => deleteTimeLog(log.id)}
                            className="shrink-0 opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-600 transition-opacity">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: metadata */}
            <div className="md:col-span-2 min-w-0 p-5 space-y-5 bg-slate-50/40">
              {!metaEditable && (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Lock className="w-3 h-3 shrink-0" />
                  Priority, Assignee &amp; Due Date can only be changed by managers or team leads.
                </div>
              )}
              {/* Status — all project members can change status */}
              <Field label="Status">
                <Select value={status} onValueChange={v => { setStatus(v); mark(); }}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              {/* Priority */}
              <Field label="Priority">
                <Select value={String(priority)} onValueChange={v => { setPriority(Number(v)); mark(); }} disabled={!metaEditable}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Low</SelectItem>
                    <SelectItem value="2">Medium</SelectItem>
                    <SelectItem value="3">High</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* Assignee */}
              <Field label={<span className="flex items-center gap-1"><User className="w-3 h-3" />Assignee</span>}>
                <Select value={assigneeId || 'unassigned'} onValueChange={v => { setAssigneeId(v === 'unassigned' ? '' : v); mark(); }} disabled={!metaEditable}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map(m => (
                      <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                        {m.full_name || m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assigneeId && (() => {
                  const m = members.find(x => (x.user_id || x.id) === assigneeId);
                  return m ? (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Avatar className="w-5 h-5">
                        <AvatarFallback className="text-[10px] bg-[#ede0ff] text-[#4B1E87]">{initials(m.full_name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground">{m.full_name}</span>
                    </div>
                  ) : null;
                })()}
              </Field>

              {/* Due Date */}
              <Field label={<span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Due Date</span>}>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={e => { setDueDate(e.target.value); mark(); }}
                  disabled={!metaEditable}
                  className={`h-8 text-sm ${isOverdue ? 'border-rose-300 text-rose-600' : ''}`}
                />
                {isOverdue && <p className="text-xs text-rose-600 mt-0.5">Overdue</p>}
              </Field>

              {/* Estimate */}
              <Field label={<span className="flex items-center gap-1"><Clock className="w-3 h-3" />Estimate (hrs)</span>}>
                <Input
                  type="number" min="0" step="0.5"
                  value={estimateHrs}
                  onChange={e => { setEstimateHrs(e.target.value); mark(); }}
                  disabled={!canManage}
                  placeholder="e.g. 4"
                  className="h-8 text-sm"
                />
              </Field>

              {/* Sprint */}
              {sprints.length > 0 && (
                <Field label={<span className="flex items-center gap-1"><Link2 className="w-3 h-3" />Sprint</span>}>
                  <Select value={sprintId || 'none'} onValueChange={v => { setSprintId(v === 'none' ? '' : v); mark(); }} disabled={!canManage}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="No sprint" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No sprint</SelectItem>
                      {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {/* Tags */}
              <Field label={<span className="flex items-center gap-1"><Tag className="w-3 h-3" />Tags</span>}>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(t => (
                      <Badge key={t} className="text-[11px] bg-[#f3eeff] text-[#4B1E87] border-[#d4b8f0] pr-1 flex items-center gap-1">
                        {t}
                        {canManage && (
                          <button onClick={() => removeTag(t)}>
                            <X className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        placeholder="Add tag…"
                        className="h-7 text-xs"
                      />
                      <Button size="sm" variant="outline" onClick={addTag} className="h-7 px-2">
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </Field>

              {/* Dependencies */}
              <Field label={<span className="flex items-center gap-1"><GitMerge className="w-3 h-3" />Blocked by</span>}>
                <div className="space-y-1.5">
                  {depTasks.map(dep => (
                    <div key={dep.id} className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${dep.status === 'done' || dep.status === 'approved' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      <span className="flex-1 truncate text-gray-700">{dep.title}</span>
                      <button onClick={() => removeDependency(dep.id)} className="text-gray-400 hover:text-rose-500 shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {canManage && (
                    <div className="relative">
                      <div className="flex gap-1 items-center">
                        <Search className="w-3 h-3 absolute left-2 text-gray-400" />
                        <input
                          value={depSearch}
                          onChange={e => setDepSearch(e.target.value)}
                          placeholder="Search tasks to block on…"
                          className="w-full text-xs border rounded-lg pl-6 pr-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {(depResults.length > 0 || depSearching) && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-36 overflow-y-auto">
                          {depSearching ? (
                            <p className="text-xs text-gray-400 px-3 py-2 animate-pulse">Searching…</p>
                          ) : depResults.map(r => (
                            <button key={r.id} onClick={() => addDependency(r)}
                              className="w-full text-left text-xs px-3 py-2 hover:bg-[#f3eeff] flex items-center gap-2">
                              <span className="truncate flex-1">{r.title}</span>
                              <span className="text-gray-400 truncate max-w-[80px]">{r.project_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Field>

              {/* Save button — visible near Due Date/Priority/Assignee fields */}
              {dirty && (
                <div className="pt-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full h-8 text-sm bg-[#4B1E87] hover:bg-[#3a1569] text-white"
                  >
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-2 border-t border-slate-200 space-y-1 text-[11px] text-muted-foreground">
                <p>Created {fmt(task.created_at)}</p>
                <p>Updated {fmt(task.updated_at)}</p>
                {task.created_by && <p>Creator ID: {task.created_by.slice(0, 8)}…</p>}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export default TaskDetailModal;
